/**
 * The form fields the property history export posts to `POST /report/job-history`.
 *
 * The export has to carry the same filters the page queried with, otherwise the
 * spreadsheet is wider or narrower than what the user was looking at. Building
 * the fields here rather than inline in the hook keeps that correspondence
 * testable, since the hook itself needs a DOM and a Firebase token.
 *
 * Note there is no field for an empty company scope: a form post cannot carry an
 * empty array, so the report endpoint treats an absent company list the same way
 * the list endpoint treats an empty one, as every company the user administers.
 */
import { endOfDay, startOfDay } from "date-fns";

type Selectable = string | { value: string };

export type JobHistoryReportFilters = {
  companies?: string[];
  dateRange?: { startDate?: Date; endDate?: Date; timezone?: string };
  services?: Selectable[];
  jobs?: Selectable[];
  cities?: Selectable[];
  operators?: Selectable[];
  routes?: Selectable[];
  servicedBy?: Selectable[];
  season?: string | null;
  flaggedForReview?: boolean | null;
  searchPhrase?: string | null;
};

const valueOf = (item: Selectable): string =>
  typeof item === "string" ? item : item.value;

export default function jobHistoryReportFields({
  companies = [],
  dateRange = {},
  services = [],
  jobs = [],
  cities = [],
  operators = [],
  routes = [],
  servicedBy = [],
  season = null,
  flaggedForReview = null,
  searchPhrase = null,
}: JobHistoryReportFilters): Array<[string, string]> {
  const fields: Array<[string, string]> = [];

  for (const companyId of companies) {
    fields.push(["filters[companies][]", companyId]);
  }

  if (dateRange.startDate && dateRange.endDate) {
    fields.push([
      "filters[from]",
      startOfDay(dateRange.startDate).toISOString(),
    ]);
    fields.push(["filters[to]", endOfDay(dateRange.endDate).toISOString()]);
    fields.push([
      "displayTimezone",
      dateRange.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    ]);
  }

  const lists: Array<[string, Selectable[]]> = [
    ["filters[services][]", services],
    ["filters[jobs][]", jobs],
    ["filters[cities][]", cities],
    ["filters[operators][]", operators],
    ["filters[routes][]", routes],
    ["filters[servicedBy][]", servicedBy],
  ];
  for (const [name, items] of lists) {
    for (const item of items) {
      fields.push([name, valueOf(item)]);
    }
  }

  if (season) fields.push(["filters[season]", season]);
  if (flaggedForReview) fields.push(["filters[flaggedForReview]", "1"]);
  if (searchPhrase) fields.push(["searchPhrase", searchPhrase]);

  return fields;
}
